import React, {useState, useEffect} from 'react';
import {Smartphone, RotateCw, Laptop, Tablet, X, MoveRight} from 'lucide-react';

const useScreenState = () => {
    const [state, setState] = useState(/** @type {'unsupported'|'rotate'|'ok'|null} */ (null));
    useEffect(() => {
        const check = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            const portrait = h > w;
            if (w >= 1024) {
                setState('ok');
            } else if (portrait && h >= 1024) {
                setState('rotate');
            } else {
                setState('unsupported');
            }
        };
        const onOrientationChange = () => setTimeout(check, 100);
        check();
        window.addEventListener('resize', check);
        window.addEventListener('orientationchange', onOrientationChange);
        return () => {
            window.removeEventListener('resize', check);
            window.removeEventListener('orientationchange', onOrientationChange);
        };
    }, []);
    return state;
};

const css = `
@keyframes sgSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}

.sg-root {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    padding: 1.5rem;
    z-index: 99999;
    font-family: "Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.sg-card {
    background: #ffffff;
    border: 3px solid #111111;
    border-radius: 10px;
    box-shadow: 6px 6px 0px 0px #111111;
    padding: 1.5rem;
    width: 100%;
    max-width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    text-align: center;
}

.sg-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 3px solid #111111;
    flex-shrink: 0;
    position: relative;
}

.sg-icon-x {
    position: absolute;
    top: -14px;
    right: -14px;
    background: #ef4444;
    color: #ffffff;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.sg-title {
    font-size: 1rem;
    font-weight: 900;
    color: #111111;
    line-height: 1.3;
    margin: 0;
}

.sg-body {
    font-size: 0.75rem;
    color: #444444;
    line-height: 1.6;
    margin: 0;
}

.sg-tips {
    display: flex;
    gap: 0.5rem;
    width: 100%;
}

.sg-tip {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    background: #f5f5f5;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    padding: 0.75rem 0.5rem;
}

.sg-tip-label {
    font-size: 0.75rem;
    font-weight: 800;
    color: #111111;
}

.sg-tip-sub {
    font-size: 0.625rem;
    font-weight: 600;
    color: #555555;
}

.sg-rotate-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background: #f9f9f9;
    border: 2px dashed #dddddd;
    border-radius: 10px;
    padding: 0.5rem 1rem;
}

.sg-spin { animation: sgSpin 2s linear infinite; }

.sg-overlay {
    position: absolute;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    padding: 1.5rem;
    font-family: "Poppins", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* Landscape phone: two-column layout */
@media (max-height: 599px) and (orientation: landscape) and (max-width: 900px) {
    .sg-card {
        flex-direction: row;
        max-width: 580px;
        text-align: left;
        align-items: center;
        gap: 1.25rem;
        padding: 1rem 1.25rem;
    }
    .sg-title { font-size: 0.85rem; }
    .sg-body  { font-size: 0.68rem; }
    .sg-tip   { padding: 0.4rem 0.5rem; }
    .sg-tip-label { font-size: 0.65rem; }
    .sg-tip-sub   { font-size: 0.55rem; }
    .sg-icon-wrap { width: 52px; height: 52px; }
}
`;

const ScreenGate = ({children}) => {
    const screenState = useScreenState();

    if (screenState === null) return null;

    if (screenState === 'unsupported') {
        return (
            <>
                <style>{css}</style>
                <div className="sg-root">
                    <div className="sg-card">
                        <div
                            className="sg-icon-wrap"
                            style={{background: '#FFF3B0'}}
                        >
                            <Smartphone size={34} color="#111111" />
                            <span className="sg-icon-x">
                                <X size={18} strokeWidth={3} />
                            </span>
                        </div>
                        <h2 className="sg-title">{'Your screen is too small!'}</h2>
                        <p className="sg-body">{'MagBlocks needs more space to work. Try it on a computer or tablet!'}</p>
                        <div className="sg-tips">
                            <div className="sg-tip">
                                <Laptop size={22} color="#111111" />
                                <span className="sg-tip-label">{'Computer'}</span>
                                <span className="sg-tip-sub">{'Best choice'}</span>
                            </div>
                            <div className="sg-tip">
                                <Tablet size={22} color="#111111" />
                                <span className="sg-tip-label">{'Tablet'}</span>
                                <span className="sg-tip-sub">{'Works great'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <style>{css}</style>
            <div style={{position: 'fixed', inset: 0}}>
                {children}
                {screenState === 'rotate' && (
                    <div className="sg-overlay">
                        <div className="sg-card">
                            <div
                                className="sg-icon-wrap"
                                style={{background: '#B8F0D8'}}
                            >
                                <RotateCw size={34} color="#111111" className="sg-spin" />
                            </div>
                            <h2 className="sg-title">{'Rotate your tablet!'}</h2>
                            <p className="sg-body">{'Landscape mode gives you a lot more room to build and create.'}</p>
                            <div className="sg-rotate-hint">
                                <Tablet size={24} color="#888888" />
                                <MoveRight size={16} color="#aaaaaa" />
                                <Tablet size={24} color="#888888" style={{transform: 'rotate(90deg)'}} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ScreenGate;
